import React from 'react';
import PrePaymentCardScanModal from './modals/PrePaymentCardScanModal';
import ActiveEntriesModal from './modals/ActiveEntriesModal';
import DirectTriggerModal from './modals/DirectTriggerModal';
import CardReturnModal from './modals/CardReturnModal';
import ClubEntryReservationsModal from './modals/ClubEntryReservationsModal';
import PostSaleCardAssignmentModal from './modals/PostSaleCardAssignmentModal';

/**
 * ClubEntryFlowModals
 *
 * QuickSaleScreen'den taşınan "Kulüp Girişi" modal grubu. QuickSaleScreen'de
 * `{isClubEntryMode && <ClubEntryFlowModals ... />}` ile çağrıldığı için guard
 * dışarıda korunur; bu component yalnızca kulüp girişi modunda mount edilir.
 *
 * Props-bag deseni QuickSaleModals.tsx ile uyumludur.
 */
interface ClubEntryFlowModalsProps {
  showPrePaymentCardModal: boolean;
  setShowPrePaymentCardModal: any;
  setPendingPaymentType: any;
  setPendingPosArgs: any;
  handlePrePaymentCardConfirm: any;
  handlePrePaymentCardSkip?: any;
  clubEntrySettings: any;
  getServiceItemsFromCart: any;
  getClubEntryCardSlots: any;
  lastCardSlots: any;
  currentUserEmail: any;
  pendingPaymentType: any;

  showActiveEntriesModal: boolean;
  setShowActiveEntriesModal: any;
  currentScreen: any;

  showDirectTriggerModal: boolean;
  setShowDirectTriggerModal: any;
  screenSettings: any;

  showCardReturnModal: boolean;
  setShowCardReturnModal: any;
  companyId: any;

  showClubReservationsModal: boolean;
  setShowClubReservationsModal: any;
  selectedCustomer: any;
  currentUser: any;
  cashPaymentCashboxId: any;
  posOptions: any;
  handleReservationPosCheckInRequest: any;

  showPostSaleCardModal: boolean;
  setShowPostSaleCardModal: any;
  lastSaleReceiptId: any;
  setLastSaleReceiptId: any;
  setLastServiceItems: any;
  clearCart: any;
  lastReferenceNumber: any;
  lastPavoPaySaleNumber: any;
  lastPaymentMethod: any;
  lastServiceItems: any;
  refreshOrderNumber: any;
}

const ClubEntryFlowModals: React.FC<ClubEntryFlowModalsProps> = ({
  showPrePaymentCardModal,
  setShowPrePaymentCardModal,
  setPendingPaymentType,
  setPendingPosArgs,
  handlePrePaymentCardConfirm,
  handlePrePaymentCardSkip,
  clubEntrySettings,
  getServiceItemsFromCart,
  getClubEntryCardSlots,
  lastCardSlots,
  currentUserEmail,
  pendingPaymentType,
  showActiveEntriesModal,
  setShowActiveEntriesModal,
  currentScreen,
  showDirectTriggerModal,
  setShowDirectTriggerModal,
  screenSettings,
  showCardReturnModal,
  setShowCardReturnModal,
  companyId,
  showClubReservationsModal,
  setShowClubReservationsModal,
  selectedCustomer,
  currentUser,
  cashPaymentCashboxId,
  posOptions,
  handleReservationPosCheckInRequest,
  showPostSaleCardModal,
  setShowPostSaleCardModal,
  lastSaleReceiptId,
  setLastSaleReceiptId,
  setLastServiceItems,
  clearCart,
  lastReferenceNumber,
  lastPavoPaySaleNumber,
  lastPaymentMethod,
  lastServiceItems,
  refreshOrderNumber,
}) => {
  return (
    <>
      <PrePaymentCardScanModal
        open={showPrePaymentCardModal}
        onClose={() => {
          setShowPrePaymentCardModal(false);
          setPendingPaymentType(null);
          setPendingPosArgs(null);
        }}
        onConfirm={handlePrePaymentCardConfirm}
        onSkip={handlePrePaymentCardSkip}
        cardMode={clubEntrySettings?.cardMode === 'FLEXIBLE' ? 'FLEXIBLE' : 'ONE_PER_RIGHT'}
        serviceItems={getServiceItemsFromCart()}
        cardSlots={showPrePaymentCardModal ? getClubEntryCardSlots() : []}
        depositEnabled={clubEntrySettings?.depositEnabled || false}
        servicePaymentMethod={pendingPaymentType === 'POS' ? 'POS' : pendingPaymentType === 'CASH' ? 'CASH' : undefined}
        prepaidMode={pendingPaymentType === 'OTHER_TICKET'}
      />
      <ActiveEntriesModal
        open={showActiveEntriesModal}
        onClose={() => setShowActiveEntriesModal(false)}
        screenId={currentScreen?.id ? parseInt(currentScreen.id, 10) : undefined}
      />
      <DirectTriggerModal
        open={showDirectTriggerModal}
        onClose={() => setShowDirectTriggerModal(false)}
        doorIds={(clubEntrySettings?.accessControlDoorIds || '').split(',').filter(Boolean)}
        localServiceUrl={screenSettings?.localServiceUrl}
      />
      <CardReturnModal
        open={showCardReturnModal}
        onClose={() => setShowCardReturnModal(false)}
        depositEnabled={clubEntrySettings?.depositEnabled || false}
        companyId={companyId || undefined}
      />
      {/* ✅ Kulüp girişi: Rezervasyonları listele + Giriş Yap akışı */}
      <ClubEntryReservationsModal
        open={showClubReservationsModal}
        onClose={() => setShowClubReservationsModal(false)}
        screenId={currentScreen?.id ? parseInt(currentScreen.id, 10) : undefined}
        clubEntrySettings={clubEntrySettings}
        selectedCustomer={selectedCustomer ? { id: selectedCustomer.id, companyName: selectedCustomer.companyName || '' } : null}
        currentUserEmail={currentUser?.email || ''}
        defaultCashboxId={cashPaymentCashboxId ? parseInt(String(cashPaymentCashboxId), 10) : null}
        defaultBankAccountId={(() => {
          const firstPos = posOptions.find((p: any) => p.bankAccountId);
          const id = firstPos?.bankAccountId;
          if (id == null) return null;
          const n = parseInt(String(id), 10);
          return Number.isFinite(n) ? n : null;
        })()}
        onPosCheckInRequest={(reservation: any, cards: any, expectedCardCount: any) => {
          setShowClubReservationsModal(false);
          handleReservationPosCheckInRequest(reservation, cards, expectedCardCount);
        }}
      />
      {showPostSaleCardModal && lastSaleReceiptId && (
        <PostSaleCardAssignmentModal
          open={showPostSaleCardModal}
          onClose={() => {
            setShowPostSaleCardModal(false);
            setLastSaleReceiptId(null);
            setLastServiceItems([]);
            clearCart();
          }}
          saleReceiptId={lastSaleReceiptId}
          saleReceiptNumber={lastReferenceNumber}
          pavoPaySaleNumber={lastPavoPaySaleNumber}
          screenId={currentScreen?.id ? parseInt(currentScreen.id, 10) : undefined}
          paymentMethod={lastPaymentMethod}
          serviceItems={lastServiceItems}
          cardSlots={lastCardSlots}
          companyId={companyId}
          currentUserEmail={currentUserEmail}
          depositAmount={clubEntrySettings?.defaultDepositAmount || 0}
          depositEnabled={clubEntrySettings?.depositEnabled || false}
          entryMode={clubEntrySettings?.entryMode || 'SINGLE_ENTRY'}
          onComplete={() => {
            refreshOrderNumber();
            clearCart();
          }}
        />
      )}
    </>
  );
};

export default ClubEntryFlowModals;
